import type { GuestSessionCredentials } from '@creativitas/shared';
import { ApiError } from '@/lib/api';

/**
 * Кем гость был в этой сессии — запоминает браузер, а не вкладка.
 *
 * Прежде подтверждение участия лежало в `sessionStorage`, а оно живёт ровно
 * столько, сколько вкладка. 17 сентября Дарина закрыла вчерашнюю вкладку на
 * iPad, открыла ту же ссылку — и платформа завела вторую «Дарину»: предъявить
 * прежнюю личность браузеру было нечем. Сервер для этого давно готов: гость
 * получает случайный токен, хранится только его хеш, и токен пускает ровно к
 * своему участнику своей сессии. Не хватало одного — не терять токен.
 *
 * Модель простая: один браузер и одно приглашение — один участник. Ключ
 * привязан к приглашению, поэтому разные сессии в одном браузере не мешают
 * друг другу. Другой браузер (Safari и встроенный браузер Telegram — разные)
 * — это другое хранилище, и там человек войдёт заново: узнать «тот же iPad»
 * сайт не может, браузеры этого сознательно не дают.
 *
 * По имени ничего не объединяется: имя — подпись, личность — участник и токен.
 */

const KEY_PREFIX = 'creativitas:guest:';

export const guestCredentialsKey = (inviteToken: string): string => `${KEY_PREFIX}${inviteToken}`;

type StorageKind = 'localStorage' | 'sessionStorage';

/**
 * Хранилище, если браузер его даёт. В приватном режиме старых Safari и при
 * запрете сайтам хранить данные само обращение к нему бросает исключение.
 */
function storage(kind: StorageKind): Storage | null {
  try {
    const target = window[kind];
    const probe = `${KEY_PREFIX}probe`;
    target.setItem(probe, '1');
    target.removeItem(probe);
    return target;
  } catch {
    return null;
  }
}

function parse(raw: string | null): GuestSessionCredentials | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<GuestSessionCredentials>;
    const valid = [value.sessionId, value.participantId, value.displayName, value.guestToken].every(
      (field) => typeof field === 'string' && field.length > 0,
    );
    return valid ? (value as GuestSessionCredentials) : null;
  } catch {
    return null;
  }
}

function read(kind: StorageKind, key: string): GuestSessionCredentials | null {
  try {
    return parse(storage(kind)?.getItem(key) ?? null);
  } catch {
    return null;
  }
}

/**
 * Данные гостя для этого приглашения.
 *
 * Сначала — браузер. Если там пусто, смотрим вкладку: так хранили до
 * 17 сентября, и уже открытые у участников вкладки не должны после обновления
 * платформы превратиться в «нового участника». Найденное во вкладке
 * переносится в браузер и дальше живёт там.
 */
export function loadGuestCredentials(inviteToken: string): GuestSessionCredentials | null {
  const key = guestCredentialsKey(inviteToken);
  const remembered = read('localStorage', key);
  if (remembered) return remembered;

  const fromTab = read('sessionStorage', key);
  if (fromTab) saveGuestCredentials(inviteToken, fromTab);
  return fromTab;
}

/** Запомнить участника в браузере; если браузер хранить не даёт — хотя бы во вкладке. */
export function saveGuestCredentials(inviteToken: string, credentials: GuestSessionCredentials): void {
  const key = guestCredentialsKey(inviteToken);
  const value = JSON.stringify(credentials);
  const local = storage('localStorage');
  try {
    if (local) {
      local.setItem(key, value);
      storage('sessionStorage')?.removeItem(key);
      return;
    }
    storage('sessionStorage')?.setItem(key, value);
  } catch {
    /* Хранилище переполнено или запрещено: участник останется до закрытия страницы. */
  }
}

/** Забыть участника этого приглашения — и в браузере, и во вкладке. */
export function forgetGuestCredentials(inviteToken: string): void {
  const key = guestCredentialsKey(inviteToken);
  for (const kind of ['localStorage', 'sessionStorage'] as const) {
    try {
      storage(kind)?.removeItem(key);
    } catch {
      /* Нечего забывать, если хранилища нет. */
    }
  }
}

/**
 * Сервер больше не признаёт этого участника: ведущий его удалил, сессия
 * исчезла или токен не подходит. Только в этих случаях запомненные данные
 * стираются.
 *
 * Обрыв связи (`status` 0) сюда не относится и стирать ничего не должен: у
 * Дарьи связь пропадала на минуты, и после неё она обязана вернуться тем же
 * участником, а не встать в форму имени.
 */
export function isRevokedGuestAccess(error: unknown): boolean {
  return error instanceof ApiError && [401, 403, 404].includes(error.status);
}
